import { Reveal, Bubbles, WaveDivider } from "@/components/site/Motion";
import { Btn } from "@/components/site/Btn";
import { ArrowRight, Zap, Building2, User, LifeBuoy, MapPin, ShieldCheck } from "lucide-react";
import { HOW, EMPLOYMENT, OFFICE, ACCESS } from "@/lib/content";

const stepColors = ["bg-sky", "bg-coral", "bg-yellow", "bg-seagreen", "bg-sky", "bg-coral", "bg-yellow", "bg-seagreen"];

export default function HowItWorks() {
  return (
    <>
      <section className="relative bg-gradient-to-b from-sky/30 to-cream pt-16 md:pt-24 pb-16 overflow-hidden" data-testid="how-hero">
        <Bubbles count={7} />
        <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
          <Reveal>
            <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean mb-4">How it works</p>
            <h1 className="font-display font-extrabold text-5xl md:text-7xl text-navy leading-[0.95]">{HOW.hero}</h1>
            <p className="font-body text-lg md:text-xl text-navy/70 mt-6 max-w-2xl">{HOW.heroSub}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="how-steps">
        <div className="max-w-5xl mx-auto px-6 md:px-10 flex flex-col gap-6 md:gap-8">
          {HOW.steps.map((s, i) => (
            <Reveal key={s.n} delay={(i % 2) * 0.05}>
              <div className="flex flex-col sm:flex-row gap-5 sm:gap-7 items-start bg-white border-2 border-navy rounded-3xl p-6 md:p-8 shadow-brutal hover:-translate-y-1 transition-transform duration-200">
                <div className={`shrink-0 w-16 h-16 rounded-2xl ${stepColors[i]} border-2 border-navy grid place-items-center font-display font-extrabold text-2xl text-navy`}>{s.n}</div>
                <div>
                  <h2 className="font-display font-bold text-2xl md:text-3xl text-navy">{s.title}</h2>
                  <p className="font-body text-lg text-navy/70 mt-2 max-w-2xl">{s.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-seagreen/12" data-testid="how-speed">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="bg-white border-2 border-navy rounded-3xl p-8 md:p-12 shadow-brutal">
              <span className="inline-grid place-items-center w-14 h-14 rounded-full bg-yellow border-2 border-navy mb-5"><Zap className="w-7 h-7" /></span>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy">{HOW.speed.headline}</h2>
              <p className="font-body text-lg text-navy/75 mt-4">{HOW.speed.body}</p>
              <p className="font-body text-navy/60 mt-3">{HOW.speed.why}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="how-office">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="bg-sky/12 border-2 border-navy rounded-3xl p-8 md:p-12 shadow-brutal grid md:grid-cols-[auto_1fr] gap-6 items-start">
              <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-sky border-2 border-navy"><MapPin className="w-7 h-7" /></span>
              <div>
                <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy">{OFFICE.headline}</h2>
                <p className="font-body text-lg text-navy/75 mt-4">{OFFICE.body}</p>
                <p className="font-display font-bold text-xl text-navy mt-5 bg-yellow inline-block px-4 py-2 rounded-xl border-2 border-navy shadow-brutal -rotate-1">{OFFICE.highlight}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-navy" data-testid="how-employment">
        <div className="bg-seagreen/12"><WaveDivider color="#082F49" /></div>
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <Reveal>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-cream max-w-3xl leading-[1.05]">{EMPLOYMENT.headline}</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {EMPLOYMENT.columns.map((c, i) => {
              const Icon = [Building2, User, LifeBuoy][i];
              const bg = ["bg-sky", "bg-yellow", "bg-seagreen"][i];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="bg-cream border-2 border-navy rounded-3xl p-7 h-full shadow-brutal-sky">
                    <span className={`inline-grid place-items-center w-12 h-12 rounded-full ${bg} border-2 border-navy`}><Icon className="w-6 h-6" /></span>
                    <h3 className="font-display font-bold text-2xl text-navy mt-4">{c.title}</h3>
                    <p className="font-body text-navy/70 mt-2">{c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <p className="font-display font-bold text-xl md:text-2xl text-yellow mt-10">{EMPLOYMENT.manage}</p>
            <p className="font-body text-cream/80 mt-3 max-w-2xl">{EMPLOYMENT.pay}</p>
            <div className="mt-8"><Btn to="/contact" variant="yellow" testid="how-cta">Tell Us What You Need <ArrowRight className="w-5 h-5" /></Btn></div>
          </Reveal>
        </div>
      </section>
      <section className="py-16 md:py-24" data-testid="how-access">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="border-2 border-navy rounded-3xl p-8 md:p-10 bg-white shadow-brutal grid md:grid-cols-[auto_1fr] gap-6 items-start">
              <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-seagreen border-2 border-navy"><ShieldCheck className="w-7 h-7" /></span>
              <div>
                <h2 className="font-display font-bold text-2xl md:text-3xl text-navy">{ACCESS.headline}</h2>
                <p className="font-body text-lg text-navy/70 mt-3">{ACCESS.body}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
